/** Editor для Поступлення з переробки (Порція 4b). 2 таблиці: materials з 206 + products на 26. */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { accounts as accountsApi } from '@/api/documents';
import {
  processingReceipts,
  type ProcessingReceiptMaterialIn,
  type ProcessingReceiptProductIn,
} from '@/api/processingReceipts';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const PERMISSION = 'production.processing.receipt.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  processor_contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  processing_service_amount: string;
  processing_service_vat: string;
  comment: string;
  materials: ProcessingReceiptMaterialIn[];
  products: ProcessingReceiptProductIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  processor_contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  processing_service_amount: '0',
  processing_service_vat: '0',
  comment: '',
  materials: [],
  products: [],
};

const newM = (n: number): ProcessingReceiptMaterialIn => ({ line_number: n, nomenclature_id: '', quantity: '0' });
const newP = (n: number): ProcessingReceiptProductIn => ({ line_number: n, nomenclature_id: '', quantity: '0' });

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function ProcessingReceiptEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.list({ limit: 1000 }) });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['processing-receipt', id],
    queryFn: () => processingReceipts.get(id!),
    enabled: !isNew,
  });
  const numberPreview = useNumberPreview('doc.processing_receipts', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        processor_contractor_id: d.processor_contractor_id,
        contract_id: d.contract_id ?? '',
        warehouse_id: d.warehouse_id,
        processing_service_amount: d.processing_service_amount,
        processing_service_vat: d.processing_service_vat,
        comment: d.comment ?? '',
        materials: d.materials.map((m) => ({ line_number: m.line_number, nomenclature_id: m.nomenclature_id, quantity: m.quantity })),
        products: d.products.map((p) => ({ line_number: p.line_number, nomenclature_id: p.nomenclature_id, quantity: p.quantity, account_code: p.account_code ?? null })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}
            disabled={isPosted} style={{ width: '100%', minWidth: 130 }}>
      <option value="">— (деф. {def})</option>
      {acctList.map((a) => (<option key={a.id} value={a.code}>{a.code} — {a.name}</option>))}
    </select>
  );

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    processor_contractor_id: form.processor_contractor_id,
    contract_id: form.contract_id || null,
    warehouse_id: form.warehouse_id,
    processing_service_amount: form.processing_service_amount,
    processing_service_vat: form.processing_service_vat,
    comment: form.comment || null,
    materials: form.materials,
    products: form.products,
  });

  const create = useMutation({
    mutationFn: () => processingReceipts.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-receipts'] });
      navigate(`/processing-receipts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => processingReceipts.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-receipts'] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await processingReceipts.create({ organization_id: form.organization_id, ...buildPayload() });
      await processingReceipts.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-receipts'] });
      navigate(`/processing-receipts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await processingReceipts.update(id!, buildPayload());
      await processingReceipts.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-receipts'] }),
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => processingReceipts.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-receipts'] }),
    onError: (e) => setError(extractError(e)),
  });

  function addM() { setForm((f) => ({ ...f, materials: [...f.materials, newM(f.materials.length + 1)] })); }
  function rmM(i: number) {
    setForm((f) => ({ ...f, materials: f.materials.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setM(i: number, patch: Partial<ProcessingReceiptMaterialIn>) {
    setForm((f) => ({ ...f, materials: f.materials.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function addP() { setForm((f) => ({ ...f, products: [...f.products, newP(f.products.length + 1)] })); }
  function rmP(i: number) {
    setForm((f) => ({ ...f, products: f.products.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setP(i: number, patch: Partial<ProcessingReceiptProductIn>) {
    setForm((f) => ({ ...f, products: f.products.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове поступлення з переробки' : `Поступлення: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено ({docQuery.data?.total_cost ?? '0'})
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/processing-receipts')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={false}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })} disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Переробник</label>
          <select value={form.processor_contractor_id}
                  onChange={(e) => setForm({ ...form, processor_contractor_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {cQ.data?.items.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Склад призначення</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.code} — {w.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Вартість послуг переробника</label>
          <input type="number" step="0.01" value={form.processing_service_amount}
                 onChange={(e) => setForm({ ...form, processing_service_amount: e.target.value })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>ПДВ на послуги</label>
          <input type="number" step="0.01" value={form.processing_service_vat}
                 onChange={(e) => setForm({ ...form, processing_service_vat: e.target.value })} disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>Сировина (повертається з 206)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Сировина</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.materials.map((m, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{m.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={m.nomenclature_id}
                        onChange={(e) => setM(i, { nomenclature_id: e.target.value })} disabled={isPosted}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={m.quantity}
                       onChange={(e) => setM(i, { quantity: e.target.value })} disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>{!isPosted && <button onClick={() => rmM(i)}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && <button onClick={addM} style={{ marginTop: '0.5rem' }}>+ Сировина</button>}

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>Готова продукція (на 26)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Продукція</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 150 }}>Рах. обліку</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.products.map((p, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{p.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={p.nomenclature_id}
                        onChange={(e) => setP(i, { nomenclature_id: e.target.value })} disabled={isPosted}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={p.quantity}
                       onChange={(e) => setP(i, { quantity: e.target.value })} disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                {acctSelect(p.account_code, (v) => setP(i, { account_code: v }), '26')}
              </td>
              <td style={{ padding: '0.5rem' }}>{!isPosted && <button onClick={() => rmP(i)}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && <button onClick={addP} style={{ marginTop: '0.5rem' }}>+ Продукція</button>}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
